/**
 * Phase 54.26 — Mobile Scanner page.
 *
 * Public route `/scan/:session_id?t=<mobile_token>`. Runs on the
 * phone. Zero ERP access — the only thing it can do is emit barcode
 * values to the specific Scanner Session it was opened for.
 *
 * Decoder priority:
 *   1) Native `BarcodeDetector` API — used when supported (Chrome/
 *      Edge on Android, Safari on some newer iOS). Fastest, zero
 *      JS decoding cost.
 *   2) Fallback: `@zxing/browser` — pure JS decoder, ~45 KB
 *      gzipped. Same UX; the operator can't tell which decoder
 *      is running.
 *
 * After a scan we vibrate (if supported), beep, flash a green
 * banner, and IMMEDIATELY resume — no taps required to keep going.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Eye, ArrowLeft, Loader2, AlertCircle, ScrollText } from "lucide-react";

const FORMATS = ["ean_13", "ean_8", "code_128", "code_39", "upc_a", "upc_e", "codabar", "itf", "qr_code"];

// Phase 54.28 — Currency helpers (mirror the ERP Price Lookup page).
const CURRENCY_ORDER = ["TRY", "EUR", "USD", "AED", "SAR", "KWD"];
const CURRENCY_FMT = {
  TRY: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 }),
  USD: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "USD", minimumFractionDigits: 2 }),
  EUR: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }),
  AED: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "AED", minimumFractionDigits: 2 }),
  SAR: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "SAR", minimumFractionDigits: 2 }),
  KWD: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "KWD", minimumFractionDigits: 3 }),
};
const STATUS_LABEL = {
  active:   { label: "Aktif",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  reserved: { label: "Rezerve",    cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  sold:     { label: "Satıldı",    cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  repair:   { label: "Tamirde",    cls: "bg-purple-500/15 text-purple-300 border-purple-500/30" },
  transfer: { label: "Transferde", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
  lost:     { label: "Kayıp",      cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

export default function ScannerMobilePage() {
  const { session_id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const stopRef = useRef(null);       // native detector loop stop
  const zxingRef = useRef(null);      // zxing controls (stop())
  const audioRef = useRef(null);
  const [status, setStatus] = useState("connecting");  // connecting | live | error | ended
  const [lastCode, setLastCode] = useState("");
  const [flash, setFlash] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  const [decoderKind, setDecoderKind] = useState("");
  const [error, setError] = useState("");
  const [wsCloseCode, setWsCloseCode] = useState(0);
  // Phase 54.28 — "Fiyatı Gör" view state. `viewProduct` is set only
  // after the user taps the button and the mobile-scoped API returns.
  // Camera remains mounted; we just hide the scan UI while the detail
  // overlay is visible so the operator can hand the phone to the
  // customer.
  const [viewProduct, setViewProduct] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  // Phase 55.4 — Sales history overlay. Only requested on tap; cleared
  // on every fresh scan so the phone never shows stale history.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyItems, setHistoryItems] = useState(null);   // null = never fetched
  const retryCountRef = useRef(0);
  const lastEmitRef = useRef({ code: "", ts: 0 });

  // Phase 54.27.1 — Turkish diagnostic labels for WebSocket close codes.
  const describeCloseCode = (code) => {
    if (code === 4401) return "Yetkisiz — mobil belirteç geçersiz.";
    if (code === 4403) return "Bu oturum başka bir kullanıcıya ait.";
    if (code === 4404) return "Oturum bulunamadı veya süresi doldu. ERP tarafından yeni bir QR üret.";
    if (code === 1006) return "Sunucuya ulaşılamıyor (ingress / proxy).";
    if (code === 1011) return "Sunucu hatası — WebSocket beklenmedik şekilde kapandı.";
    if (code === 1000) return "Bağlantı kapatıldı.";
    return `WebSocket kapandı (kod ${code}).`;
  };

  const beep = () => {
    try {
      if (!audioRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        audioRef.current = new AC();
      }
      const ctx = audioRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine"; osc.frequency.value = 1200;
      gain.gain.value = 0.1;
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.08);
    } catch { /* noop */ }
  };

  const onScan = (code) => {
    const trimmed = String(code || "").trim();
    if (!trimmed) return;
    // De-dupe: don't emit the same code more than once per 1200 ms.
    const now = Date.now();
    if (lastEmitRef.current.code === trimmed && (now - lastEmitRef.current.ts) < 1200) return;
    lastEmitRef.current = { code: trimmed, ts: now };
    setLastCode(trimmed);
    setScanCount((n) => n + 1);
    setFlash(true); setTimeout(() => setFlash(false), 250);
    try { navigator.vibrate?.(45); } catch { /* noop */ }
    beep();
    // Any prior product view is stale after a fresh scan — clear it so
    // the "Fiyatı Gör" button reflects the newest code. We do NOT
    // auto-open the view; the operator must tap the button.
    setViewProduct(null); setViewError("");
    // 55.4 — same rule for sales history.
    setHistoryOpen(false); setHistoryItems(null); setHistoryError("");
    if (wsRef.current && wsRef.current.readyState === 1) {
      try { wsRef.current.send(JSON.stringify({ type: "barcode", code: trimmed })); } catch { /* noop */ }
    }
  };

  // Phase 54.28 — "Fiyatı Gör" fetch. Uses the session-token-gated
  // mobile lookup endpoint so we don't need an ERP JWT on the phone.
  // Same payload shape as the ERP Price Lookup page so the render code
  // can consume it directly.
  const openPriceView = useCallback(async () => {
    if (!lastCode) return;
    setViewLoading(true); setViewError(""); setViewProduct(null);
    try {
      const backend = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL || "";
      const url = `${backend}/api/usm/scan/sessions/${encodeURIComponent(session_id)}/lookup?t=${encodeURIComponent(token)}&code=${encodeURIComponent(lastCode)}`;
      const r = await fetch(url);
      if (!r.ok) {
        let msg = "Ürün bulunamadı";
        try { const j = await r.json(); msg = j.detail || msg; } catch { /* noop */ }
        setViewError(msg);
        return;
      }
      const data = await r.json();
      setViewProduct(data);
    } catch (e) {
      setViewError(e?.message || "Bağlantı hatası");
    } finally {
      setViewLoading(false);
    }
  }, [lastCode, session_id, token]);

  const closePriceView = () => {
    setViewProduct(null);
    setViewError("");
    setViewLoading(false);
  };

  // Phase 55.4 — Sales history overlay. Only fires on user tap; reuses
  // the mobile session token (no ERP JWT needed) and delegates the
  // matching to the shared Phase 55.3 stock-code lookup on the backend.
  const openSalesHistory = useCallback(async () => {
    if (!lastCode) return;
    setHistoryOpen(true); setHistoryLoading(true); setHistoryError(""); setHistoryItems(null);
    try {
      const backend = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL || "";
      const url = `${backend}/api/usm/scan/sessions/${encodeURIComponent(session_id)}/sales-history?t=${encodeURIComponent(token)}&code=${encodeURIComponent(lastCode)}`;
      const r = await fetch(url);
      if (!r.ok) {
        let msg = "Satış geçmişi alınamadı";
        try { const j = await r.json(); msg = j.detail || msg; } catch { /* noop */ }
        setHistoryError(msg);
        return;
      }
      const data = await r.json();
      setHistoryItems(data.items || []);
    } catch (e) {
      setHistoryError(e?.message || "Bağlantı hatası");
    } finally {
      setHistoryLoading(false);
    }
  }, [lastCode, session_id, token]);
  const closeSalesHistory = () => {
    setHistoryOpen(false); setHistoryError(""); setHistoryItems(null); setHistoryLoading(false);
  };

  // WebSocket connect + auto-reconnect
  useEffect(() => {
    let retry = 0; let closed = false;
    // Phase 54.27.1 — Anchor to the SAME origin the user opened, not a
    // baked-in REACT_APP_BACKEND_URL that could be stale in production.
    const backend = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = `${backend.replace(/^http/, "ws")}/api/usm/scan/ws/mobile/${session_id}?t=${encodeURIComponent(token)}`;
    const connect = () => {
      if (closed) return;
      let ws;
      try { ws = new WebSocket(wsUrl); }
      catch (e) {
        console.error("mobile WS construction failed", e);
        setStatus("error");
        setError("WebSocket URL geçersiz veya karışık içerik engellendi.");
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => { retry = 0; retryCountRef.current = 0; setStatus("live"); setError(""); setWsCloseCode(0); };
      ws.onerror = (ev) => { console.warn("mobile WS error", ev); };
      ws.onclose = (ev) => {
        wsRef.current = null;
        setWsCloseCode(ev.code || 0);
        // Auth / session failures — stop immediately with the real reason.
        if (ev.code === 4404 || ev.code === 4401 || ev.code === 4403) {
          setStatus("ended");
          setError(describeCloseCode(ev.code));
          return;
        }
        // Bounded retries so we never spin forever.
        retryCountRef.current = retry;
        if (retry >= 4) {
          setStatus("error");
          setError(`${describeCloseCode(ev.code)} · Yeniden bağlanma başarısız (${retry}/4). Sayfayı yeniden yükleyin veya ERP tarayıcı düğmesinden yeniden başlatın.`);
          return;
        }
        setStatus("connecting");
        setError(`${describeCloseCode(ev.code)} · Yeniden deneniyor (${retry + 1}/4)…`);
        const bo = Math.min(15000, 1000 * Math.pow(2, retry++));
        setTimeout(connect, bo);
      };
    };
    connect();
    return () => { closed = true; try { wsRef.current?.close(); } catch { /* noop */ } };
  }, [session_id, token]);

  // Camera + decoder
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        v.setAttribute("playsinline", "true");
        await v.play();

        // Prefer native BarcodeDetector.
        if ("BarcodeDetector" in window) {
          try {
            const supported = await window.BarcodeDetector.getSupportedFormats();
            const usable = FORMATS.filter((f) => supported.includes(f));
            if (usable.length) {
              const det = new window.BarcodeDetector({ formats: usable });
              setDecoderKind("Native");
              let stopped = false;
              const loop = async () => {
                if (stopped || cancelled) return;
                try {
                  const codes = await det.detect(v);
                  if (codes && codes.length) onScan(codes[0].rawValue);
                } catch { /* noop */ }
                requestAnimationFrame(() => setTimeout(loop, 120));
              };
              stopRef.current = () => { stopped = true; };
              loop();
              return;
            }
          } catch { /* fall through to zxing */ }
        }

        // ZXing fallback.
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        setDecoderKind("ZXing");
        const controls = await reader.decodeFromVideoElement(v, (result) => {
          if (result) onScan(result.getText());
        });
        zxingRef.current = controls;
      } catch (e) {
        console.error(e);
        setStatus("error");
        setError("Kamera erişilemedi. Tarayıcı iznini kontrol et.");
      }
    })();
    return () => {
      cancelled = true;
      try { stopRef.current?.(); } catch { /* noop */ }
      try { zxingRef.current?.stop?.(); } catch { /* noop */ }
      const s = videoRef.current?.srcObject;
      s?.getTracks?.().forEach(t => { try { t.stop(); } catch { /* noop */ } });
    };
  }, []);

  const priceViewOpen = viewLoading || viewProduct || viewError;
  const salesHistoryOpen = historyOpen;

  return (
    <div className="min-h-[100dvh] bg-black text-white flex flex-col" data-testid="scanner-mobile-page">
      <div className="p-3 flex items-center justify-between text-[11px] uppercase tracking-[0.15em]">
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${status === "live" ? "bg-green-500" : status === "ended" || status === "error" ? "bg-red-500" : "bg-yellow-500 animate-pulse"}`} />
          <span data-testid="mobile-scan-status">{status === "live" ? "Bağlı" : status === "ended" ? "Kapandı" : status === "error" ? "Hata" : "Bağlanıyor…"}</span>
        </div>
        {decoderKind && <span className="text-[9px] text-white/50 tracking-wider">{decoderKind}</span>}
      </div>

      {/* Camera view — hidden while the price detail overlay is open, but
          NEVER unmounted (that would tear down the getUserMedia stream
          and the WebSocket). Returning to it via "Yeni Barkod Tara" is
          therefore instant with no re-permission prompt. */}
      <div className={`relative flex-1 min-h-0 bg-black overflow-hidden ${(priceViewOpen || salesHistoryOpen) ? "hidden" : ""}`}>
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
        {/* Reticle */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`border-2 ${flash ? "border-green-400" : "border-[#C8A96A]"} transition-colors w-[80%] max-w-[420px] aspect-[16/9] rounded`} />
        </div>
        {flash && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-green-500 text-black text-[11px] tracking-wider uppercase px-3 py-1 rounded" data-testid="mobile-scan-flash">
            {lastCode}
          </div>
        )}
      </div>

      {/* Scan meta strip + "Fiyatı Gör" button — camera-mode only. */}
      {!priceViewOpen && !salesHistoryOpen && (
        <>
          <div className="p-3 grid grid-cols-2 gap-2 text-center bg-black">
            <div className="border border-white/10 py-2">
              <div className="text-[9px] uppercase tracking-widest text-white/50">Son</div>
              <div className="text-[13px] font-mono truncate" data-testid="mobile-last-code">{lastCode || "—"}</div>
            </div>
            <div className="border border-white/10 py-2">
              <div className="text-[9px] uppercase tracking-widest text-white/50">Tarama</div>
              <div className="text-[13px] font-mono" data-testid="mobile-scan-count">{scanCount}</div>
            </div>
          </div>
          {lastCode && (
            <div className="px-3 pb-3 bg-black space-y-2">
              <button
                type="button"
                data-testid="mobile-view-price-btn"
                onClick={openPriceView}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C8A96A] hover:bg-[#B8994D] active:scale-[0.99] text-black font-medium text-[15px] tracking-wide py-4 rounded transition"
              >
                <Eye size={18} strokeWidth={2} /> Fiyatı Gör
              </button>
              {/* Phase 55.4 — Sales history for the scanned product. */}
              <button
                type="button"
                data-testid="mobile-sales-history-btn"
                onClick={openSalesHistory}
                className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 active:scale-[0.99] text-white font-medium text-[15px] tracking-wide py-4 rounded border border-white/20 transition"
              >
                <ScrollText size={18} strokeWidth={2} /> Satış Geçmişini Gör
              </button>
            </div>
          )}
        </>
      )}

      {/* Product detail overlay — Phase 54.28. Consumes the same
          payload shape as the ERP Price Lookup page. Camera continues
          streaming in the background (hidden) so returning is instant. */}
      {priceViewOpen && (
        <ProductDetailView
          loading={viewLoading}
          error={viewError}
          product={viewProduct}
          scannedCode={lastCode}
          onBack={closePriceView}
          onRetry={openPriceView}
        />
      )}

      {/* Phase 55.4 — Sales history overlay for the scanned product. */}
      {salesHistoryOpen && (
        <SalesHistoryView
          loading={historyLoading}
          error={historyError}
          items={historyItems}
          scannedCode={lastCode}
          onBack={closeSalesHistory}
          onRetry={openSalesHistory}
        />
      )}

      {error && (
        <div className="p-3 bg-red-600/20 border-t border-red-600/40 text-[11px] space-y-2" data-testid="mobile-scan-error">
          <div>{error}</div>
          {(status === "error" || status === "ended") && (
            <button
              type="button"
              data-testid="mobile-reload-btn"
              onClick={() => { try { window.location.reload(); } catch { /* noop */ } }}
              className="text-[10px] uppercase tracking-[0.18em] bg-white/10 hover:bg-white/20 border border-white/20 px-3 py-1.5"
            >
              Sayfayı Yenile
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Phase 54.28 — Detail view. Deliberately mirrors the ERP Price Lookup
// design language (currency wall, meta rows, subtle badge) but in the
// dark scanner palette so the customer sees a coherent experience.
function ProductDetailView({ loading, error, product, scannedCode, onBack, onRetry }) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-black" data-testid="mobile-price-view">
      <div className="max-w-lg mx-auto p-4 pb-24">
        {loading && (
          <div className="text-center py-16 text-white/70">
            <Loader2 size={28} className="mx-auto animate-spin text-[#C8A96A]" />
            <p className="text-[12px] uppercase tracking-[0.2em] mt-3">Aranıyor…</p>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-500/10 border border-red-500/30 p-4 flex items-start gap-3" data-testid="mobile-price-view-error">
            <AlertCircle size={18} className="text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-red-300">Bulunamadı</div>
              <div className="text-[11px] text-red-200/80 mt-1 break-words">{error}</div>
              {scannedCode && (
                <div className="text-[11px] text-red-200/60 mt-1 font-mono">Kod: {scannedCode}</div>
              )}
              <button
                type="button"
                data-testid="mobile-price-retry-btn"
                onClick={onRetry}
                className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] bg-red-600/70 hover:bg-red-600 text-white px-3 py-1.5"
              >Yeniden Dene</button>
            </div>
          </div>
        )}

        {!loading && !error && product && (
          <ProductCard product={product} />
        )}
      </div>

      {/* Fixed footer with "Yeni Barkod Tara" — always visible so the
          operator can reset without scrolling. */}
      <div className="fixed inset-x-0 bottom-0 bg-black/95 backdrop-blur border-t border-white/10 p-3">
        <div className="max-w-lg mx-auto">
          <button
            type="button"
            data-testid="mobile-back-to-scan-btn"
            onClick={onBack}
            className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 active:scale-[0.99] border border-white/15 text-white text-[13px] uppercase tracking-[0.15em] py-3.5 rounded transition"
          >
            <ArrowLeft size={16} /> Yeni Barkod Tara
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductCard({ product }) {
  const photo = product.photo_urls?.[0];
  const status = STATUS_LABEL[product.status] || { label: product.status || "—", cls: "bg-white/10 text-white/80 border-white/15" };
  const prices = product.prices || {};
  return (
    <div className="space-y-4" data-testid="mobile-product-card">
      {/* Image */}
      <div className="w-full aspect-square bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
        {photo ? (
          <img src={photo} alt={product.name} className="w-full h-full object-contain p-3" />
        ) : (
          <div className="text-white/40 text-[11px] uppercase tracking-[0.2em]">Görsel yok</div>
        )}
      </div>

      {/* Stock code + name + status badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[15px] tracking-wider text-white/90" data-testid="mobile-product-stock-code">
            {product.stock_code || "—"}
          </div>
          <h2 className="font-heading text-[22px] leading-tight mt-1 text-white break-words" data-testid="mobile-product-name">
            {product.name}
          </h2>
        </div>
        <span className={`shrink-0 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border rounded-sm ${status.cls}`} data-testid="mobile-product-status">
          {status.label}
        </span>
      </div>

      {/* Meta rows */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-y border-white/10 py-4 text-[13px]">
        <Meta label="Materyal / Ayar" value={product.material || "—"} />
        <Meta label="Ağırlık" value={product.weight_grams != null ? `${product.weight_grams} gr` : "—"} />
        <Meta label="Kategori" value={product.category?.name_tr || "—"} />
        <Meta label="Şube" value={product.branch?.name || "—"} />
      </div>

      {/* Currency wall — mirrors ERP Price Lookup ordering & rounding. */}
      <div className="grid grid-cols-2 gap-2" data-testid="mobile-price-wall">
        {CURRENCY_ORDER.map((c) => {
          const amt = prices[c];
          const dp = c === "KWD" ? 3 : 2;
          const formatted = amt != null && Number.isFinite(amt)
            ? new Intl.NumberFormat("tr-TR", { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(amt)
            : "—";
          const primary = c === "TRY";
          return (
            <div
              key={c}
              data-testid={`mobile-price-tile-${c}`}
              className={
                "border rounded-sm px-3 py-3 " +
                (primary
                  ? "border-[#C8A96A] bg-[#C8A96A]/10"
                  : "border-white/10 bg-white/5")
              }
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">{c}</div>
              <div
                className={
                  "font-finance tabular-nums whitespace-nowrap overflow-hidden text-ellipsis " +
                  (primary ? "text-[#F5E6C8] text-[18px] font-semibold" : "text-white text-[15px] font-medium")
                }
                title={formatted}
              >
                {formatted}
              </div>
            </div>
          );
        })}
      </div>

      {prices.rates_as_of && (
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">
          TCMB: {new Date(prices.rates_as_of).toLocaleString("tr-TR")}
        </p>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">{label}</div>
      <div className="text-white text-[13.5px] break-words">{value}</div>
    </div>
  );
}



// Phase 55.4 — Sales history mobile overlay. Same visual language as
// the Fiyatı Gör detail view: dark background, gold accents, big tap
// targets. The eye icon deep-links into the existing ERP Sale Detail
// route (`/usm/sales/:sid`) — no bespoke detail component.
function SalesHistoryView({ loading, error, items, scannedCode, onBack, onRetry }) {
  const openSaleDetail = (sid) => {
    try { window.location.href = `/usm/sales/${encodeURIComponent(sid)}`; } catch { /* noop */ }
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return String(iso).slice(0, 10); }
  };
  const fmtMoney = (n) => {
    const v = Number(n || 0);
    try { return v.toLocaleString("tr-TR", { style: "currency", currency: "TRY" }); }
    catch { return `₺${v.toFixed(2)}`; }
  };
  const payLabel = (t) => ({
    cash: "Nakit", card: "Kredi", pos: "POS", credit_card: "Kredi Kartı",
    bank: "Havale", transfer: "Havale", installment: "Taksit",
    fx: "Döviz", other: "Diğer",
  }[String(t || "").toLowerCase()] || (t || "—"));
  const statusChip = (s) => {
    const norm = String(s || "").toLowerCase();
    if (norm === "voided") return { label: "İptal", cls: "bg-red-500/15 text-red-300 ring-red-400/30" };
    if (norm === "refunded") return { label: "İade", cls: "bg-amber-500/15 text-amber-200 ring-amber-400/30" };
    return { label: "Tamamlandı", cls: "bg-emerald-500/15 text-emerald-200 ring-emerald-400/30" };
  };

  return (
    <div className="absolute inset-0 bg-black flex flex-col text-white z-10" data-testid="mobile-sales-history-view">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 bg-black">
        <button onClick={onBack} className="p-2 -ml-2 text-white/80" data-testid="mobile-sales-history-back">
          <ArrowLeft size={20} strokeWidth={2} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">Satış Geçmişi</div>
          <div className="text-[15px] font-medium truncate text-[#C8A96A]" data-testid="mobile-sales-history-code">{scannedCode || "—"}</div>
        </div>
      </div>

      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={22} className="animate-spin text-[#C8A96A]" />
        </div>
      )}

      {!loading && error && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-3 text-center">
          <AlertCircle size={28} className="text-red-400" />
          <div className="text-[13px] text-white/80" data-testid="mobile-sales-history-error">{error}</div>
          <button onClick={onRetry} className="mt-2 px-4 py-2 bg-[#C8A96A] text-black rounded text-[13px] font-medium">Tekrar Dene</button>
        </div>
      )}

      {!loading && !error && Array.isArray(items) && items.length === 0 && (
        <div className="flex-1 flex items-center justify-center px-8 text-center text-white/60 text-[13.5px]" data-testid="mobile-sales-history-empty">
          Bu ürün için satış geçmişi bulunamadı.
        </div>
      )}

      {!loading && !error && Array.isArray(items) && items.length > 0 && (
        <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-white/5" data-testid="mobile-sales-history-list">
          {items.map((s) => {
            const chip = statusChip(s.status);
            return (
              <li key={s.id} className="px-4 py-3" data-testid={`mobile-sales-history-item-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[15px] font-mono text-[#C8A96A]">{s.sale_no || "—"}</span>
                      <span className="text-[11px] text-white/50">{fmtDate(s.created_at)}</span>
                      <span className={`inline-flex items-center text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded ring-1 ${chip.cls}`}>{chip.label}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-[12px] text-white/70">
                      <span>{s.branch_name || "—"}</span>
                      <span className="text-white/30">·</span>
                      <span>{s.employee_name || "—"}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className="text-[16px] font-finance tabular-nums text-white">{fmtMoney(s.grand_total)}</span>
                      {s.payment_type && (
                        <span className="text-[10.5px] uppercase tracking-widest text-white/50 border border-white/15 rounded px-1.5 py-0.5">{payLabel(s.payment_type)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => openSaleDetail(s.id)}
                    className="shrink-0 w-11 h-11 rounded flex items-center justify-center bg-white/10 hover:bg-white/15 active:scale-[0.97] transition"
                    data-testid={`mobile-sales-history-open-${s.id}`}
                    title="Satış detayını aç"
                  >
                    <Eye size={18} strokeWidth={2} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
